/**
 * ObjectDesignerNode — V2 (Sprint 9i migration)
 *
 * Migrated to use UnifiedBottomPanel with 3 tabs:
 * Config, Refine, Variants
 *
 * Width: 400px. Image aspect: 4:3 (aspect-[4/3]).
 */

import React, { memo, useState, useCallback, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { Position, NodeProps, useReactFlow, Handle } from 'reactflow'
import { AnimatePresence, motion } from 'framer-motion'
import { getThumbnailUrl, getAssetUrl } from '../../../../api/useAssetUrl'
import { executeNode } from '../../../../api/client'
import { useCanvasOptional } from '../../CanvasContext'
import { useNodeHover } from '../../NodeHoverContext'
import {
  AgentNodeToolbar,
  FloatingNodeLabel,
  UnifiedBottomPanel,
  OBJECT_TABS,
  DEFAULT_TAB,
  ConfigTabContent,
  RefineTabContent,
  VariantsTabContent,
  useNodeActions,
  ShimmerPlaceholder,
  TypewriterText,
  useSequencedLoading,
  useBottomPanelAutoOpen,
  type ToolbarAction,
  type HandleConfig,
  type SelectedReference,
  buildAssetCandidates,
  type HistoryEntry,
} from '../shared'
import { DEFAULT_STYLE } from '../../../../constants/styles'
import { useFeedbackMode } from '../../panels/useFeedbackMode'
import { AGENT_COLORS } from '../../../../constants/agentColors'
import { useProjectId } from '../../../../contexts/ProjectIdContext'
import { cn } from '../../../../lib/utils'
import { MessageSquarePlus, BookmarkPlus } from 'lucide-react'

import type { ObjectDesignerNodeData } from '../../../../generated/nodeDataTypes'

export type { ObjectDesignerNodeData }

const HANDLES: HandleConfig[] = [
  { id: 'in_upstream', type: 'target', position: Position.Top },
  { id: 'in_reference_images', type: 'target', position: Position.Left },
  { id: 'out_object_image', type: 'source', position: Position.Bottom },
  { id: 'out_image_path', type: 'source', position: Position.Bottom },
  { id: 'out_metadata', type: 'source', position: Position.Right },
]

const agentColor = AGENT_COLORS.object

const ObjectDesignerNode = memo(function ObjectDesignerNode(
  props: NodeProps<ObjectDesignerNodeData>
) {
  const { id, data, selected, dragging } = props
  const {
    cacheKey,
    in_name = '',
    in_style = DEFAULT_STYLE,
    in_description = '',
    in_reference_images,
    out_object_image,
    status = 'idle',
    progressMessage,
    error,
  } = data
  const projectId = useProjectId()

  const { setNodes, getNodes } = useReactFlow()
  const canvas = useCanvasOptional()
  const { handleDelete, handleDuplicate, handleDownload, handleAddToChat, handleSaveToLibrary } = useNodeActions({ nodeId: id })
  const { setHoveredNodeId } = useNodeHover()
  const [isHovered, setIsHovered] = useState(false)

  // Image URL resolution
  const mainImg = out_object_image?.images?.main
  let imagePath: string | undefined
  if (mainImg?.history?.length) {
    const idx = mainImg.picked_index ?? 0
    imagePath = mainImg.history[idx]?.result?.local_path
  }
  const cacheBust = cacheKey
  const imageUrl =
    imagePath && projectId ? getThumbnailUrl(projectId, imagePath, 0, cacheBust) : null
  const fullImageUrl = imagePath && projectId ? getAssetUrl(projectId, imagePath, 0) : null

  const isRunning = status === 'running'

  const {
    showSubtitleShimmer,
    subtitleActive,
    onSubtitleComplete,
  } = useSequencedLoading({ isRunning })

  // Bottom panel state
  const [bottomPanelOpen, setBottomPanelOpen] = useState(false)
  const [activeTab, setActiveTab] = useState(DEFAULT_TAB)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const openPanelToTab = useCallback(
    (tab: string) => {
      setActiveTab(tab)
      setBottomPanelOpen(true)
      setNodes((nodes) => nodes.map((n) => ({ ...n, selected: n.id === id })))
    },
    [id, setNodes]
  )

  // History state
  const mainImageHistory = out_object_image?.images?.main
  const historyEntries = useMemo(
    () => (mainImageHistory?.history ?? []) as HistoryEntry[],
    [mainImageHistory?.history]
  )
  const pickedIndex = mainImageHistory?.picked_index ?? 0
  const batchPending = isRunning ? (data.batch_pending ?? 0) : 0
  const hasHistory = historyEntries.length > 1 || batchPending > 0

  // Shared auto-open behavior
  useBottomPanelAutoOpen({
    selected,
    isRunning,
    batchPending,
    setBottomPanelOpen,
    setActiveTab,
  })

  // Feedback mode
  const { feedbackText, setFeedbackText, handleRefine } = useFeedbackMode({
    nodeId: id,
    status,
  })

  // History variant selection
  const handleHistorySelect = useCallback(
    (index: number) => {
      const entry = historyEntries[index]
      const localPath = entry?.result?.local_path
      if (!localPath) return

      const currentNode = getNodes().find((n) => n.id === id)
      const currentObjImage = currentNode?.data?.out_object_image
      const newCacheKey = String(Date.now())
      const updatedObjImage = {
        ...currentObjImage,
        images: {
          ...currentObjImage?.images,
          main: {
            ...currentObjImage?.images?.main,
            picked_index: index,
          },
        },
      }

      setNodes((nds) =>
        nds.map((n) =>
          n.id !== id
            ? n
            : {
                ...n,
                data: {
                  ...n.data,
                  out_object_image: updatedObjImage,
                  cacheKey: newCacheKey,
                },
              }
        )
      )
      canvas?.updateNodeData(id, {
        out_object_image: updatedObjImage,
        cacheKey: newCacheKey,
      })
    },
    [id, historyEntries, getNodes, setNodes, canvas]
  )

  const handleDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newDesc = e.target.value
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, in_description: newDesc } } : n
        )
      )
      canvas?.updateNodeData(id, { in_description: newDesc })
    },
    [id, setNodes, canvas]
  )

  // Config generate handler
  const handleConfigGenerate = useCallback(
    (retryCount: number) => {
      if (isRunning || !projectId) return
      executeNode(projectId, id, undefined, retryCount)
    },
    [isRunning, projectId, id]
  )

  // Refine handler
  const onRefineSubmit = useCallback(() => {
    handleRefine()
  }, [handleRefine])

  // Style change handler
  const handleStyleChange = useCallback(
    (styleId: string) => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, in_style: styleId } } : n
        )
      )
      canvas?.updateNodeData(id, { in_style: styleId })
    },
    [id, setNodes, canvas]
  )

  // Reference handling for ConfigTabContent
  const currentRefs = useMemo<SelectedReference[]>(() => {
    if (!in_reference_images?.length) return []
    const candidates = buildAssetCandidates(getNodes(), id)
    const pathToCandidate = new Map(candidates.map((c) => [c.assetPath, c]))
    return in_reference_images.map((path, i) => {
      const match = pathToCandidate.get(path)
      return {
        nodeId: match?.nodeId ?? `ref-${i}`,
        nodeType: match?.nodeType ?? 'image',
        label: match?.label ?? path.split('/').pop() ?? `Reference ${i + 1}`,
        assetPath: path,
        mediaType: (match?.mediaType ?? 'image') as 'image' | 'video',
      }
    })
  }, [in_reference_images, getNodes, id])

  const handleReferencesChange = useCallback(
    (refs: SelectedReference[]) => {
      const paths = refs.map((r) => r.assetPath)
      const newValue = paths.length > 0 ? paths : undefined
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, in_reference_images: newValue } } : n
        )
      )
      canvas?.updateNodeData(id, { in_reference_images: newValue })
    },
    [id, setNodes, canvas]
  )

  // Prompt change handler
  const handlePromptChange = useCallback(
    (value: string) => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, in_description: value } } : n
        )
      )
      canvas?.updateNodeData(id, { in_description: value })
    },
    [id, setNodes, canvas]
  )

  // Rename handler for FloatingNodeLabel
  const handleRename = useCallback(
    (newName: string) => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id ? { ...n, data: { ...n.data, in_name: newName } } : n
        )
      )
      canvas?.updateNodeData(id, { in_name: newName })
    },
    [id, setNodes, canvas],
  )

  // Download handler
  const onDownload = useCallback(() => {
    if (fullImageUrl) {
      handleDownload(fullImageUrl, imagePath?.split('/').pop() || 'object.jpg')
    }
  }, [fullImageUrl, imagePath, handleDownload])

  // Toolbar actions
  const toolbarActions: ToolbarAction[] = useMemo(
    () => [
      {
        key: 'history',
        label: 'Variants',
        icon: (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
            />
          </svg>
        ),
        onClick: () => openPanelToTab('variants'),
        hidden: !hasHistory,
      },
      {
        key: 'addToChat',
        label: 'Add to Chat',
        icon: <MessageSquarePlus size={14} />,
        onClick: handleAddToChat,
      },
      {
        key: 'save-to-library',
        label: 'Save to Library',
        icon: <BookmarkPlus className="w-4 h-4" />,
        onClick: handleSaveToLibrary,
        group: true,
      },
      {
        key: 'duplicate',
        label: 'Duplicate',
        icon: (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
        ),
        onClick: handleDuplicate,
        group: true,
      },
      {
        key: 'delete',
        label: 'Delete',
        icon: (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
        ),
        onClick: handleDelete,
        variant: 'danger' as const,
      },
    ],
    [hasHistory, handleDuplicate, handleDelete, handleAddToChat, handleSaveToLibrary, openPanelToTab]
  )

  // Border color state machine
  const computedBorderStyle: React.CSSProperties = {}
  if (selected) {
    computedBorderStyle.borderColor = agentColor.active
    computedBorderStyle.boxShadow = agentColor.glow
  } else if (status === 'running') {
    computedBorderStyle.borderColor = agentColor.active
  } else if (isHovered) {
    computedBorderStyle.borderColor = agentColor.focus
  } else {
    computedBorderStyle.borderColor = agentColor.hover
  }

  // Panel visibility
  const panelVisible = !!selected && !dragging && bottomPanelOpen && (!isRunning || batchPending > 0)

  return (
    <>
      <div className="relative">
        <AgentNodeToolbar actions={toolbarActions} isVisible={!!selected && !dragging} />

        {/* Floating label above the card */}
        <FloatingNodeLabel name={in_name} fallback="Untitled Object" onRename={handleRename} />

        {/* Main Card */}
        <div
          data-testid="object-designer-node"
          className={cn(
            'relative rounded-[24px] border bg-surface-0 transition-all duration-200 overflow-hidden cursor-pointer',
            status === 'running' && 'animate-node-shimmer'
          )}
          style={{ width: 400, ...computedBorderStyle }}
          onMouseEnter={() => {
            setIsHovered(true)
            setHoveredNodeId(id)
          }}
          onMouseLeave={() => {
            setIsHovered(false)
            setHoveredNodeId(null)
          }}
        >
          {/* ReactFlow Handles */}
          {HANDLES.map((handle) => (
            <Handle
              key={handle.id}
              type={handle.type}
              position={handle.position}
              id={handle.id}
              className="!bg-transparent !border-0 !w-2 !h-2"
            />
          ))}

          {/* Image Area -- 4:3 aspect ratio */}
          <div className="relative group p-1.5">
            <AnimatePresence mode="wait">
              {imageUrl && (
                <motion.img
                  key={imagePath}
                  src={imageUrl}
                  alt="Object"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2, ease: 'easeInOut' }}
                  className={cn(
                    'w-full aspect-[4/3] object-cover block rounded-[20px]',
                    isRunning && 'blur-sm brightness-50 transition-all duration-300'
                  )}
                />
              )}
            </AnimatePresence>

            {isRunning && imageUrl && (
              <div className="absolute inset-1.5 rounded-[20px] flex flex-col items-center justify-center">
                <div
                  className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin"
                  style={{ borderColor: agentColor.active, borderTopColor: 'transparent' }}
                />
                <span className="text-sm text-white mt-3 font-medium">Refining object</span>
              </div>
            )}

            {!imageUrl && isRunning && (
              <div className="w-full aspect-[4/3] bg-surface-1/50 rounded-[20px] flex flex-col items-center justify-center">
                <div
                  className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin"
                  style={{ borderColor: agentColor.active, borderTopColor: 'transparent' }}
                />
                <span className="text-xxs text-text-muted mt-3 text-center">
                  {progressMessage ||
                    `Creating${data.in_name ? ` ${data.in_name}` : ' object'}...`}
                </span>
              </div>
            )}

            {!imageUrl && !isRunning && (
              <div className="w-full aspect-[4/3] bg-surface-1/50 rounded-[20px] flex flex-col items-center justify-center">
                <svg
                  className="w-8 h-8 text-text-muted/20"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                  />
                </svg>
                <span className="text-xxs text-text-muted/40 mt-1">No object yet</span>
              </div>
            )}

            {imageUrl && !isRunning && (
              <div className="absolute top-[14px] right-[14px] flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setIsModalOpen(true)
                  }}
                  className="w-7 h-7 rounded bg-black/60 hover:bg-black/80 text-white flex items-center justify-center backdrop-blur-sm transition-colors nodrag"
                  title="Expand"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 8V4m0 0h4M20 8V4m0 0h-4M4 16v4m0 0h4M20 16v4m0 0h-4"
                    />
                  </svg>
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    onDownload()
                  }}
                  className="w-7 h-7 rounded bg-black/60 hover:bg-black/80 text-white flex items-center justify-center backdrop-blur-sm transition-colors nodrag"
                  title="Download"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                </button>
              </div>
            )}
          </div>

          {/* Content Area */}
          <div className="pt-2 px-6 pb-6">
            <span className="text-xs font-medium" style={{ color: agentColor.active }}>
              Object
            </span>

            {/* Description -- editable */}
            <div className="mt-2 h-[60px]">
              {showSubtitleShimmer ? (
                <div className="space-y-1.5">
                  <ShimmerPlaceholder width="100%" height={14} />
                  <ShimmerPlaceholder width="80%" height={14} />
                </div>
              ) : isRunning && in_description ? (
                <TypewriterText
                  text={in_description}
                  isActive={subtitleActive}
                  onComplete={onSubtitleComplete}
                  className="text-sm text-muted-foreground leading-relaxed line-clamp-3"
                  as="p"
                />
              ) : (
                <textarea
                  data-testid="object-description-input"
                  aria-label="Object description"
                  value={in_description || ''}
                  placeholder="Describe this object..."
                  onChange={handleDescriptionChange}
                  className="w-full h-full text-sm text-muted-foreground leading-relaxed bg-transparent border-none outline-none p-0 m-0 resize-none nodrag nowheel"
                  rows={3}
                />
              )}
            </div>
          </div>

          {/* Quick Info Bar -- 3 columns: Config | Variants | Reference */}
          <div className="grid grid-cols-3 border-t border-border-subtle px-6">
            {/* Config Column */}
            <button
              data-testid="quick-info-config"
              onClick={(e) => {
                e.stopPropagation()
                openPanelToTab('config')
              }}
              className="py-3 flex flex-col items-start gap-1.5 hover:bg-surface-1 transition-colors nodrag border-r border-border-subtle pr-3"
            >
              <span className="text-sm font-medium text-foreground">Config</span>
              <div className="flex items-center gap-1.5">
                <svg className="w-3.5 h-3.5 text-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
            </button>

            {/* Variants Column */}
            <button
              data-testid="quick-info-variants"
              onClick={(e) => {
                e.stopPropagation()
                openPanelToTab('variants')
              }}
              className="px-3 py-3 flex flex-col items-start gap-1.5 hover:bg-surface-1 transition-colors nodrag border-r border-border-subtle"
            >
              <span className="text-sm font-medium text-foreground">Variants</span>
              <div className="flex items-center gap-1.5">
                <span
                  className="w-2 h-2 rounded-full"
                  style={{
                    backgroundColor:
                      historyEntries.length > 0 ? agentColor.active : 'var(--text-muted)',
                  }}
                />
                <span className="text-sm font-medium text-foreground">
                  {historyEntries.length}
                </span>
              </div>
            </button>

            {/* Reference Column */}
            <button
              data-testid="quick-info-reference"
              onClick={(e) => {
                e.stopPropagation()
                openPanelToTab('config')
              }}
              className="pl-3 py-3 flex flex-col items-start gap-1.5 hover:bg-surface-1 transition-colors nodrag"
            >
              <span className="text-sm font-medium text-foreground">Reference</span>
              <div className="flex items-center gap-1">
                {(in_reference_images || []).slice(0, 2).map((path, i) => {
                  const thumbUrl = projectId
                    ? getThumbnailUrl(projectId, path, 32, cacheBust)
                    : null
                  return thumbUrl ? (
                    <div key={i} className="w-6 h-6 rounded overflow-hidden border border-border">
                      <img src={thumbUrl} alt="" className="w-full h-full object-cover" />
                    </div>
                  ) : null
                })}
              </div>
            </button>
          </div>

          {/* Error footer */}
          {error && (
            <div className="px-3 py-2 bg-destructive/10 border-t border-destructive/30">
              <span className="text-xxs text-destructive line-clamp-2 cursor-help" title={error}>
                {error}
              </span>
            </div>
          )}
        </div>

        {/* Unified Bottom Panel */}
        <UnifiedBottomPanel
          tabs={OBJECT_TABS}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          isVisible={panelVisible}
        >
          {(tab) => {
            if (tab === 'config' && projectId) {
              return (
                <ConfigTabContent
                  nodeId={id}
                  nodeType="object"
                  prompt={in_description}
                  onPromptChange={handlePromptChange}
                  references={currentRefs}
                  onReferencesChange={handleReferencesChange}
                  currentStyleId={in_style}
                  onStyleChange={handleStyleChange}
                  isRunning={isRunning}
                  onGenerate={handleConfigGenerate}
                />
              )
            }
            if (tab === 'refine') {
              return (
                <RefineTabContent
                  feedbackText={feedbackText}
                  onFeedbackTextChange={setFeedbackText}
                  onSubmit={onRefineSubmit}
                  isRunning={isRunning}
                  nodeType="objectDesigner"
                  selectedStyleId={in_style}
                  onStyleChange={handleStyleChange}
                />
              )
            }
            if (tab === 'variants') {
              return (
                <VariantsTabContent
                  history={historyEntries}
                  pickedIndex={pickedIndex}
                  onSelectVariant={handleHistorySelect}
                  batchPending={batchPending}
                  cacheKey={cacheBust}
                  projectId={projectId}
                  accentColor="cyan"
                  aspectRatio="4/3"
                />
              )
            }
            return null
          }}
        </UnifiedBottomPanel>
      </div>

      {/* Fullscreen Lightbox */}
      {isModalOpen &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] bg-black/90 backdrop-blur-sm flex items-center justify-center p-8"
            onClick={() => setIsModalOpen(false)}
          >
            <div
              className="relative w-full max-w-6xl max-h-full flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setIsModalOpen(false)}
                className="absolute -top-12 right-0 text-text-secondary hover:text-foreground transition-colors"
              >
                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
              <img
                src={fullImageUrl ?? undefined}
                alt="Full size"
                className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
              />
            </div>
          </div>,
          document.body
        )}
    </>
  )
})

ObjectDesignerNode.displayName = 'ObjectDesignerNode'
export default ObjectDesignerNode
